/**
 * SessionsView Component
 *
 * Main view for agent sessions with split layout support.
 * Shows either a single focused session or multiple sessions in split view.
 *
 * @module components/session/SessionsView
 */

import { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { Bot, Plus, Grid2X2, RefreshCw, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { ChatPanel } from './ChatPanel.jsx';
import { SplitLayout } from './SplitLayout.jsx';
import { useAgentChat } from '../../contexts/AgentChatContext.jsx';
import { useUI } from '../../contexts/UIContext.jsx';
import { useWorkspaceSession } from '../../contexts/WorkspaceSessionContext.jsx';

// ============================================
// Session Tab Bar
// ============================================

function SessionTabBar({ agents, activeAgents, tabOrder, focusedAgentId, onFocus, onClose, onAddToSplit, splitSessions, allAgents, onAddSession }) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showAgentMenu, setShowAgentMenu] = useState(false);

  // Get all sessions to check which agents are already assigned
  const { sessions, activeSession } = useWorkspaceSession();

  // Check scroll state
  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 1);
  }, []);

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(container);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        resizeObserver.disconnect();
      };
    }
  }, [checkScroll, tabOrder]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollAmount = 200;
    container.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
  };

  // Get all agent IDs that are already assigned to OTHER sessions
  const agentsInOtherSessions = useMemo(() => {
    const assignedAgentIds = new Set();
    for (const session of sessions) {
      // Skip the current active session
      if (session.id === activeSession?.id) continue;
      // Add all agents from other sessions
      for (const agentId of session.agentIds || []) {
        assignedAgentIds.add(agentId);
      }
    }
    return assignedAgentIds;
  }, [sessions, activeSession?.id]);

  // Available agents (not in current tabs AND not assigned to other sessions)
  const availableAgents = useMemo(() => {
    return Object.values(allAgents || {}).filter(a =>
      a.status === 'active' &&
      !tabOrder.includes(a.id) &&
      !agentsInOtherSessions.has(a.id)
    );
  }, [allAgents, tabOrder, agentsInOtherSessions]);

  return (
    <div className="flex items-center bg-gray-800/50 border-b border-gray-700 flex-shrink-0">
      {/* Scroll left button */}
      {canScrollLeft && (
        <button
          onClick={() => scroll(-1)}
          className="flex-shrink-0 p-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          title="Scroll left"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}

      {/* Scrollable tabs container */}
      <div
        ref={scrollContainerRef}
        className="flex-1 flex items-center gap-1.5 px-2 py-2 overflow-x-auto scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {tabOrder.map((agentId) => {
          const agent = agents[agentId];
          const session = activeAgents[agentId];
          const isFocused = agentId === focusedAgentId;
          const isInSplit = splitSessions.includes(agentId);
          const isActive = session?.active;
          const isProcessing = session?.isProcessing;
          const unreadCount = session?.unreadCount || 0;

          return (
            <div
              key={agentId}
              className={`
                group flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all flex-shrink-0
                ${isFocused ? 'bg-indigo-600 text-white' : 'bg-gray-700/80 hover:bg-gray-600 text-gray-300'}
                ${isInSplit ? 'ring-1 ring-indigo-500/50' : ''}
              `}
              onClick={() => onFocus(agentId)}
              title={agent?.displayName || agent?.name || agentId}
            >
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-green-500' : 'bg-gray-500'}`} />
              <span className="text-sm font-medium truncate max-w-[150px]">
                {agent?.displayName || agent?.name || agentId}
              </span>
              {isProcessing && (
                <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse flex-shrink-0" />
              )}
              {unreadCount > 0 && !isFocused && (
                <span className="px-1.5 py-0.5 text-[10px] bg-red-500 text-white rounded-full flex-shrink-0">
                  {unreadCount}
                </span>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onAddToSplit(agentId); }}
                className={`p-0.5 rounded hover:bg-white/20 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${isInSplit ? 'text-indigo-300 opacity-100' : 'text-gray-400'}`}
                title={isInSplit ? 'Remove from split' : 'Add to split view'}
              >
                <Grid2X2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onClose(agentId); }}
                className="p-0.5 rounded hover:bg-white/20 text-gray-400 hover:text-red-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Close session"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Scroll right button */}
      {canScrollRight && (
        <button
          onClick={() => scroll(1)}
          className="flex-shrink-0 p-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          title="Scroll right"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Add new session button */}
      <div className="relative flex-shrink-0 border-l border-gray-700">
        <button
          onClick={() => setShowAgentMenu(!showAgentMenu)}
          className="flex items-center gap-1.5 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          title="Open new session"
        >
          <Plus className="w-4 h-4" />
        </button>

        {showAgentMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowAgentMenu(false)} />
            <div className="absolute top-full right-0 mt-1 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
              <div className="p-2 border-b border-gray-700 text-xs text-gray-500 uppercase tracking-wider">
                Open Agent Session
              </div>
              {availableAgents.length === 0 ? (
                <div className="p-3 text-sm text-gray-500 text-center">
                  No more agents available
                </div>
              ) : (
                availableAgents.map((agent) => (
                  <button
                    key={agent.id}
                    onClick={() => {
                      onAddSession(agent.id);
                      setShowAgentMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-700 transition-colors text-left"
                  >
                    <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-200 truncate">
                        {agent.displayName || agent.name}
                      </div>
                      {agent.workingDirectory && (
                        <div className="text-xs text-gray-500 truncate">
                          {agent.workingDirectory}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// Agent Selector
// ============================================

function AgentSelector({ agents, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);
  const { sessions, activeSession } = useWorkspaceSession();

  // Get all agent IDs that are already assigned to ANY session
  const agentsInSessions = useMemo(() => {
    const assignedAgentIds = new Set();
    for (const session of sessions) {
      for (const agentId of session.agentIds || []) {
        assignedAgentIds.add(agentId);
      }
    }
    return assignedAgentIds;
  }, [sessions]);

  // Available agents (active AND not assigned to any session)
  const availableAgents = useMemo(() => {
    return Object.values(agents).filter(a =>
      a.status === 'active' && !agentsInSessions.has(a.id)
    );
  }, [agents, agentsInSessions]);

  if (availableAgents.length === 0) {
    return (
      <div className="text-gray-500 text-sm">
        No active agents available
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span>Open Session</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="p-2 border-b border-gray-700 text-xs text-gray-500 uppercase tracking-wider">
            Active Agents
          </div>
          {availableAgents.map((agent) => (
            <button
              key={agent.id}
              onClick={() => {
                onSelect(agent.id);
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-700 transition-colors text-left"
            >
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-200 truncate">
                  {agent.displayName || agent.name}
                </div>
                {agent.workingDirectory && (
                  <div className="text-xs text-gray-500 truncate">
                    {agent.workingDirectory}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Empty State
// ============================================

function EmptyState({ agents, onSelectAgent }) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <Bot className="w-16 h-16 mx-auto mb-4 text-gray-600" />
        <h2 className="text-xl font-semibold text-gray-300 mb-2">
          No Active Sessions
        </h2>
        <p className="text-gray-500 mb-6">
          Select an agent to start chatting. Active agents with running sessions can be observed here.
        </p>
        <AgentSelector agents={agents} onSelect={onSelectAgent} />
      </div>
    </div>
  );
}

// ============================================
// Main SessionsView Component
// ============================================

export function SessionsView({ agents = {} }) {
  const { agentSessions, observeAgent, closeAgentSession, isConnected } = useAgentChat();
  const { tabOrder, splitSessions, focusedAgentId, setFocusedAgentId, addTab, removeTab, toggleSplit, clearSplit } = useUI();

  // Track which agents we've already started observing to avoid duplicates
  const observedAgentsRef = useRef(new Set());

  // Observe agents from tabOrder when WebSocket connects (restore from WorkspaceSession)
  useEffect(() => {
    if (!isConnected) return;

    // Find agents in tabOrder that aren't being observed yet
    const agentsToObserve = tabOrder.filter(agentId =>
      !observedAgentsRef.current.has(agentId) && !agentSessions[agentId]
    );

    if (agentsToObserve.length > 0) {
      console.log('[SessionsView] Restoring agents from WorkspaceSession:', agentsToObserve);
      agentsToObserve.forEach(agentId => {
        observedAgentsRef.current.add(agentId);
        observeAgent(agentId);
      });
    }
  }, [isConnected, tabOrder, agentSessions, observeAgent]);

  // Handle opening a new agent session
  const handleSelectAgent = useCallback((agentId) => {
    observedAgentsRef.current.add(agentId);
    observeAgent(agentId);
  }, [observeAgent]);

  // Handle closing a session
  const handleCloseSession = useCallback((agentId) => {
    closeAgentSession(agentId);
  }, [closeAgentSession]);

  // Render a single panel for an agent
  const renderPanel = useCallback((agentId) => {
    return <ChatPanel agentId={agentId} agent={agents[agentId]} />;
  }, [agents]);

  // Determine what to show
  const hasOpenSessions = tabOrder.length > 0;
  const hasSplitView = splitSessions.length > 1;

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Tab bar - always show when we have sessions, includes + button */}
      {hasOpenSessions && (
        <SessionTabBar
          agents={agents}
          activeAgents={agentSessions}
          tabOrder={tabOrder}
          focusedAgentId={focusedAgentId}
          onFocus={setFocusedAgentId}
          onClose={handleCloseSession}
          onAddToSplit={toggleSplit}
          splitSessions={splitSessions}
          allAgents={agents}
          onAddSession={handleSelectAgent}
        />
      )}

      {/* Main content area - must be flex-1 min-h-0 to contain scrollable content */}
      <div className="flex-1 min-h-0 overflow-hidden relative">
        {!hasOpenSessions ? (
          <EmptyState agents={agents} onSelectAgent={handleSelectAgent} />
        ) : hasSplitView ? (
          <SplitLayout
            splitSessions={splitSessions}
            renderPanel={renderPanel}
            agents={agents}
          />
        ) : (
          // Render ALL open sessions but only show the focused one
          // This preserves scroll position and component state across tab switches
          tabOrder.map(agentId => (
            <div
              key={agentId}
              className={`absolute inset-0 ${agentId === focusedAgentId ? 'visible' : 'invisible pointer-events-none'}`}
              style={{ zIndex: agentId === focusedAgentId ? 1 : 0 }}
            >
              <ChatPanel agentId={agentId} agent={agents[agentId]} />
            </div>
          ))
        )}
        {hasOpenSessions && !hasSplitView && !focusedAgentId && (
          <EmptyState agents={agents} onSelectAgent={handleSelectAgent} />
        )}
      </div>
    </div>
  );
}

export default SessionsView;
